import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  Alert,
  Spinner,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DocumentUploadZone, { DocumentUploadZoneRef } from '../Companies/Components/DocumentUploadZone';
import DocumentList from '../Companies/Components/DocumentList';
import RequiredDocumentsChecklist from '../Companies/Components/RequiredDocumentsChecklist';
import DocumentPreviewPanel from '../Companies/Components/DocumentPreviewPanel';
import {
  DocumentOwnerType,
  IndividualDocumentType,
} from '../../../types/documents';
import { documentsService } from '../../../services/documentsService';

interface Phase2DocumentsFormProps {
  onSubmit: (values: any) => Promise<void>;
  onPrevious: () => void;
  initialValues: any;
  isSubmitting: boolean;
  individualId?: string;
}

const Phase2DocumentsForm: React.FC<Phase2DocumentsFormProps> = ({
  onSubmit,
  onPrevious,
  initialValues,
  isSubmitting,
  individualId,
}) => {
  const { t } = useTranslation();
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [filterDocumentType, setFilterDocumentType] = useState<IndividualDocumentType | null>(null);
  const [individualDocumentsData, setIndividualDocumentsData] = useState<any | null>(null);
  const [processingDocumentId, setProcessingDocumentId] = useState<string | null>(null);

  const documentUploadZoneRef = useRef<DocumentUploadZoneRef>(null);

  // Replace Document Modal
  const [replaceModal, setReplaceModal] = useState<{
    isOpen: boolean;
    document: any | null;
  }>({ isOpen: false, document: null });
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [isReplacing, setIsReplacing] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(24);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Load individual documents
  const loadIndividualDocuments = useCallback(async (silent = false) => {
    if (!individualId) return;

    try {
      if (!silent) setLoading(true);
      const data = await documentsService.getIndividualDocuments(
        individualId,
        undefined,
        undefined,
        includeDeleted
      );
      setIndividualDocumentsData(data);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('Erro ao carregar documentos')
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [individualId, includeDeleted, t]);

  // Load documents when individualId or includeDeleted changes
  useEffect(() => {
    if (individualId) {
      loadIndividualDocuments();
    }
  }, [includeDeleted, individualId, loadIndividualDocuments]);

  // Handle individual document upload
  const handleIndividualDocumentUpload = useCallback(async (
    file: File,
    documentType: any,
    onProgress: (progress: number) => void
  ) => {
    if (!individualId) {
      toast.error(t('ID da pessoa física não disponível'));
      return;
    }

    await documentsService.uploadIndividualDocument({
      individualId,
      file,
      documentType: documentType as IndividualDocumentType,
      onProgress,
    });
    await loadIndividualDocuments(true); // Silent refresh
  }, [individualId, t, loadIndividualDocuments]);

  // Handle document download
  const handleDocumentDownload = (document: any) => {
    if (document.signedUrl) {
      documentsService.downloadDocument(document.signedUrl, document.originalFileName);
    } else {
      toast.error(t('URL de download não disponível'));
    }
  };

  // Handle document preview
  const handleDocumentPreview = (document: any) => {
    if (document.signedUrl) {
      documentsService.previewDocument(document.signedUrl);
    } else {
      toast.error(t('URL de visualização não disponível'));
    }
  };

  // Timer effect for replace operation
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isReplacing && elapsedTime < estimatedTime) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isReplacing, elapsedTime, estimatedTime]);

  // Handle replace document
  const handleReplaceClick = (document: any) => {
    setReplaceModal({ isOpen: true, document });
    setReplaceFile(null);
    setReplaceProgress(0);
    setEstimatedTime(24);
    setElapsedTime(0);
  };

  const handleReplaceConfirm = async () => {
    if (!replaceModal.document || !replaceFile) return;

    try {
      setIsReplacing(true);
      setElapsedTime(0);

      await documentsService.replaceDocument({
        documentId: replaceModal.document.id,
        ownerType: DocumentOwnerType.Individual,
        file: replaceFile,
        onProgress: (progress) => setReplaceProgress(progress),
      });

      toast.success(t('Documento substituído com sucesso'));
      setReplaceModal({ isOpen: false, document: null });
      setReplaceFile(null);
      setReplaceProgress(0);
      setElapsedTime(0);

      // Reload documents
      await loadIndividualDocuments(true);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('Erro ao substituir documento')
      );
    } finally {
      setIsReplacing(false);
      setElapsedTime(0);
    }
  };

  // Handle delete document
  const handleDocumentDelete = async (document: any, reason: string) => {
    try {
      setProcessingDocumentId(document.id);

      await documentsService.deleteDocument({
        documentId: document.id,
        ownerType: DocumentOwnerType.Individual,
        deleteReason: reason,
      });

      toast.success(t('Documento excluído com sucesso'));
      await loadIndividualDocuments(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao excluir documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle restore document
  const handleDocumentRestore = async (document: any, reason: string) => {
    try {
      setProcessingDocumentId(document.id);

      await documentsService.restoreDocument({
        documentId: document.id,
        ownerType: DocumentOwnerType.Individual,
        restoreReason: reason,
      });

      toast.success(t('Documento restaurado com sucesso'));
      await loadIndividualDocuments(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao restaurar documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle document type change (reclassify document)
  const handleDocumentTypeChange = async (document: any, newType: any) => {
    try {
      setProcessingDocumentId(document.id);

      await documentsService.updateDocumentType({
        documentId: document.id,
        ownerType: DocumentOwnerType.Individual,
        newDocumentType: newType,
      } as any);

      toast.success(t('Tipo de documento atualizado com sucesso'));
      await loadIndividualDocuments(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao atualizar tipo de documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle filename update
  const handleFileNameUpdate = async (document: any, newFileName: string) => {
    try {
      setProcessingDocumentId(document.id);

      await documentsService.updateDocumentFileName({
        documentId: document.id,
        ownerType: DocumentOwnerType.Individual,
        newFileName,
      });

      toast.success(t('Nome do arquivo atualizado com sucesso'));
      await loadIndividualDocuments(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao atualizar nome do arquivo'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate pending files before submitting
    if (documentUploadZoneRef.current) {
      // Check if there are pending files that need to be classified
      if (documentUploadZoneRef.current.hasPendingFiles()) {
        const isValid = documentUploadZoneRef.current.validatePendingFiles();
        if (!isValid) {
          return; // Stop submission if validation fails
        }

        // If there are pending files that are classified, upload them automatically
        toast.info(t('Enviando documentos classificados...'));
        await documentUploadZoneRef.current.uploadAllFiles();
      }
    }

    await onSubmit({});
  };

  return (
    <form onSubmit={handleSubmit}>
      {loading && (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted mt-2">{t('Carregando documentos...')}</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Upload Zone Toggle */}
          {individualId && individualDocumentsData && (
            <Row className="mb-3">
              <Col>
                <Button
                  color="primary"
                  onClick={() => setShowUploadZone(!showUploadZone)}
                >
                  <i
                    className={`ri-${
                      showUploadZone ? 'arrow-up' : 'upload'
                    }-line me-1`}
                  ></i>
                  {showUploadZone ? t('Ocultar Upload') : t('Upload de Documentos')}
                </Button>
                <Button
                  color="light"
                  className="ms-2"
                  onClick={() => setIncludeDeleted(!includeDeleted)}
                >
                  <i className="ri-eye-line me-1"></i>
                  {includeDeleted
                    ? t('Ocultar Excluídos')
                    : t('Mostrar Excluídos')}
                </Button>
                {filterDocumentType !== null && (
                  <Button
                    color="secondary"
                    outline
                    className="ms-2"
                    onClick={() => setFilterDocumentType(null)}
                  >
                    <i className="ri-close-line me-1"></i>
                    {t('Limpar Filtro')}
                  </Button>
                )}
              </Col>
            </Row>
          )}

          {/* Upload Zone */}
          {individualId && showUploadZone && (
            <DocumentUploadZone
              ref={documentUploadZoneRef}
              ownerType={DocumentOwnerType.Individual}
              ownerId={individualId}
              companyId={individualId} // For Individual, we use individualId as companyId placeholder
              onUpload={handleIndividualDocumentUpload}
              onUploadComplete={() => {
                setShowUploadZone(false);
                toast.success(t('Documentos enviados com sucesso!'));
                loadIndividualDocuments(true);
              }}
            />
          )}

          {/* Main Content with 2-column layout */}
          {individualId && individualDocumentsData && (
            <Row>
              {/* Left Column: Required Documents + Document List */}
              {!isPreviewExpanded && (
                <Col xxl={selectedDocument ? 9 : 12} lg={selectedDocument ? 8 : 12}>
                  {/* Required Documents Checklist */}
                  <div className="mb-3">
                    <RequiredDocumentsChecklist
                      ownerType={DocumentOwnerType.Individual}
                      documents={individualDocumentsData.documents || []}
                      onDocumentClick={(docType) => {
                        setFilterDocumentType(docType as any);
                        setSelectedDocument(null);
                      }}
                    />
                  </div>

                  {/* Document List */}
                  <div>
                    <DocumentList
                      ownerType={DocumentOwnerType.Individual}
                      documents={
                        filterDocumentType !== null
                          ? (individualDocumentsData.documents || []).filter(
                              (doc: any) => doc.documentType === filterDocumentType
                            )
                          : individualDocumentsData.documents || []
                      }
                      onDownload={handleDocumentDownload}
                      onPreview={handleDocumentPreview}
                      onDocumentSelect={setSelectedDocument}
                      onReplace={handleReplaceClick}
                      onDelete={handleDocumentDelete}
                      onRestore={handleDocumentRestore}
                      onDocumentTypeChange={handleDocumentTypeChange as any}
                      onFileNameUpdate={handleFileNameUpdate}
                      includeDeleted={includeDeleted}
                      isAdmin={true}
                      processingDocumentId={processingDocumentId}
                    />
                  </div>
                </Col>
              )}

              {/* Right Column: Document Preview Panel (Fixed or Expanded) */}
              {selectedDocument && (
                <Col xxl={isPreviewExpanded ? 12 : 3} lg={isPreviewExpanded ? 12 : 4} className="mb-3">
                  <div style={{ position: 'sticky', top: '20px' }}>
                    <DocumentPreviewPanel
                      document={selectedDocument}
                      ownerType={DocumentOwnerType.Individual}
                      isExpanded={isPreviewExpanded}
                      onClose={() => {
                        setSelectedDocument(null);
                        setIsPreviewExpanded(false);
                      }}
                      onToggleExpand={() => setIsPreviewExpanded(!isPreviewExpanded)}
                      onDownload={() => handleDocumentDownload(selectedDocument)}
                      onReplace={() => handleReplaceClick(selectedDocument)}
                      onDelete={() => {
                        const reason = prompt(t('Motivo da exclusão:'));
                        if (reason) {
                          handleDocumentDelete(selectedDocument, reason);
                          setSelectedDocument(null);
                          setIsPreviewExpanded(false);
                        }
                      }}
                      isAdmin={true}
                    />
                  </div>
                </Col>
              )}
            </Row>
          )}

          {!individualId && (
            <Card className="mb-3">
              <CardBody>
                <div className="alert alert-warning">
                  <i className="ri-alert-line me-2"></i>
                  {t('Complete a Fase 1 (Informações Pessoais) primeiro para habilitar o upload de documentos.')}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      <div className="d-flex justify-content-between gap-2 mt-4">
        <Button
          type="button"
          color="light"
          onClick={onPrevious}
          disabled={isSubmitting}
        >
          <i className="ri-arrow-left-line me-1"></i>
          {t('Anterior')}
        </Button>

        <Button
          type="submit"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('Salvando...')}
            </>
          ) : (
            <>
              {t('Próximo')}
              <i className="ri-arrow-right-line ms-1"></i>
            </>
          )}
        </Button>
      </div>

      {/* Replace Document Modal */}
      <Modal
        isOpen={replaceModal.isOpen}
        toggle={() => setReplaceModal({ isOpen: false, document: null })}
      >
        <ModalHeader
          toggle={() => setReplaceModal({ isOpen: false, document: null })}
        >
          {t('Substituir Documento')}
        </ModalHeader>
        <ModalBody>
          <p>
            {t('Substituir o documento')}{' '}
            <strong>{replaceModal.document?.originalFileName}</strong>
          </p>
          <FormGroup>
            <Label for="replaceFile">{t('Selecione o novo arquivo')}</Label>
            <Input
              type="file"
              id="replaceFile"
              onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
            />
          </FormGroup>
          {isReplacing && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">
                  {t('Substituindo documento...')} {replaceProgress}%
                </span>
                <span className="badge bg-info">
                  <i className="ri-time-line me-1"></i>
                  {elapsedTime}s / ~{estimatedTime}s
                </span>
              </div>
              <div className="progress mb-3" style={{ height: '25px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${replaceProgress}%` }}
                >
                  {replaceProgress}%
                </div>
              </div>
              <Alert color="info" className="mb-0">
                <i className="ri-information-line me-2"></i>
                <small>
                  {t('Esta operação pode levar até 24 segundos para mover o arquivo no armazenamento R2.')}
                </small>
              </Alert>
            </div>
          )}
          {!isReplacing && (
            <Alert color="info" className="mb-0">
              <i className="ri-time-line me-2"></i>
              <small>
                {t('Tempo estimado: ~24 segundos (movimentação de arquivo no armazenamento)')}
              </small>
            </Alert>
          )}
          <Alert color="warning" className="mt-3">
            <i className="ri-alert-line me-2"></i>
            {t(
              'O arquivo anterior será movido para a pasta de exclusão e a versão será incrementada.'
            )}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setReplaceModal({ isOpen: false, document: null })}
            disabled={isReplacing}
          >
            {t('Cancelar')}
          </Button>
          <Button
            color="primary"
            onClick={handleReplaceConfirm}
            disabled={!replaceFile || isReplacing}
          >
            <i className="ri-refresh-line me-1"></i>
            {t('Substituir')}
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

export default Phase2DocumentsForm;
